/**
 * Schedule List Component
 *
 * Displays synced Calendly schedules with management options
 */

import { Clock, Star, ExternalLink, Loader2, Calendar } from 'lucide-react'
import {
  useCalendlySchedules,
  useSetDefaultSchedule,
  type CalendlySchedule,
} from '@/lib/integrations/calendly'

export interface ScheduleListProps {
  onScheduleSelect?: (schedule: CalendlySchedule) => void
  selectable?: boolean
  selectedScheduleId?: string
}

export function ScheduleList({
  onScheduleSelect,
  selectable = false,
  selectedScheduleId,
}: ScheduleListProps) {
  const { data: schedules, isLoading } = useCalendlySchedules()
  const setDefault = useSetDefaultSchedule()

  const handleSetDefault = async (scheduleId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await setDefault.mutateAsync(scheduleId)
    } catch (error) {
      console.error('Failed to set default:', error)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    )
  }

  if (!schedules || schedules.length === 0) {
    return (
      <div className="text-center py-8">
        <Calendar className="w-10 h-10 text-zinc-300 dark:text-zinc-600 mx-auto mb-3" />
        <p className="text-zinc-900 dark:text-zinc-100 font-medium mb-1">No schedules found</p>
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          Make sure you have active event types in Calendly, then click "Sync Schedules" above.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {schedules.map((schedule) => (
        <div
          key={schedule.id}
          onClick={() => selectable && onScheduleSelect?.(schedule)}
          className={`
            p-4 rounded-lg border transition-all
            ${selectable ? 'cursor-pointer' : ''}
            ${
              selectedScheduleId === schedule.id
                ? 'border-[#FF6F00] bg-[#FF6F00]/5'
                : 'border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700'
            }
            ${selectable ? 'hover:bg-zinc-50 dark:hover:bg-zinc-800/50' : ''}
          `}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {schedule.color && (
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: schedule.color }}
                  />
                )}
                <h4 className="font-medium text-zinc-900 dark:text-zinc-100 truncate">
                  {schedule.name}
                </h4>
                {schedule.is_default && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/40">
                    <Star className="w-3 h-3" />
                    Default
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3 text-sm text-zinc-500 dark:text-zinc-400">
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  {schedule.duration_minutes} min
                </span>
                <a
                  href={schedule.scheduling_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center gap-1 hover:text-[#FF6F00] transition-colors"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Preview
                </a>
              </div>

              {schedule.description && (
                <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400 line-clamp-2">
                  {schedule.description}
                </p>
              )}
            </div>

            {!schedule.is_default && !selectable && (
              <button
                onClick={(e) => handleSetDefault(schedule.id, e)}
                disabled={setDefault.isPending}
                className="flex-shrink-0 text-xs text-zinc-500 dark:text-zinc-400 hover:text-[#FF6F00] transition-colors"
              >
                Set as default
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
